import {
  AccentButton,
  Card,
  CardSubtitle,
  CardTitle,
  FailedState,
  FooterRow,
  GhostButton,
  Inset,
  ListRow,
  LoadingSkeleton,
  MutedNote,
  OutlineChip,
  Screen,
  SectionLabel,
  Segmented,
  SegmentedOption,
  SectionRule,
  SimulatedBadge,
  StatusChip,
  TabularCell,
  css,
} from '@pointfive/ui';
import type { Plan, Plans, Invoice } from '@pointfive/contracts';
import { useState } from 'react';
import { api } from '@/client/api';
import { useProcedure } from '@/client/useProcedure';

/** Billing & Plans — MOD-BILLING, route `/manage/billing` (Baseline M-02, row 27).
 *
 *  A composed screen. The design package draws the *entry point* — "Billing" sits in the header's account menu with a
 *  caret (reference line 244) — but never draws the destination, so there is no reference markup to transpile and none
 *  to diff against. It is therefore built the way DEC-015 says an unrefined surface should be: out of `@pointfive/ui`,
 *  whose components carry the literals of the five canonical surfaces verbatim. Nothing here invents a style.
 *
 *  It is also the first **per-screen read** (DEC-024): the plan cards and invoices are fetched when the screen opens
 *  rather than being folded into the aggregate every other page load pays for.
 *
 *  What it says about money is deliberately careful. The payment provider is simulated in this phase, so the screen
 *  says so with the standard badge, and a checkout returns `pending_webhook` — because nothing is granted locally, an
 *  entitlement changes only when the provider confirms payment. Showing a plan as active the moment someone clicked
 *  would be a lie the rest of the system would then have to keep. */

const TIER_ORDER: Record<string, number> = { free: 0, starter: 1, trader: 2, pro: 3 };

/** Paying yearly charges at least 20% less per month — the rate is rounded down to a whole dollar, so the real saving
 *  is a shade more. "At least" rather than a bare "20%" because a customer who does the arithmetic should find the
 *  number in their favour, not against it. */
const YEARLY_SAVING_PCT = 20;

/** Prices show their cents when they have any. A yearly rate of $23.20 rendered as "$23" is a price the customer was
 *  not quoted, and on a billing screen that is not a rounding choice — it is a wrong number. */
function money(amount: number, currency: string): string {
  if (amount === 0) return 'Free';
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  const symbol = currency === 'USD' ? '$' : '';
  return `${symbol}${amount.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function InvoiceStatus({ status }: { status: Invoice['status'] }) {
  const tone =
    status === 'paid'
      ? { fg: 'var(--pos)', bg: 'color-mix(in srgb, var(--pos) 12%, transparent)' }
      : status === 'open'
        ? { fg: 'var(--amb)', bg: 'color-mix(in srgb, var(--amb) 12%, transparent)' }
        : { fg: 'var(--muted)', bg: 'var(--l5)' };
  return (
    <StatusChip fg={tone.fg} bg={tone.bg}>
      {status}
    </StatusChip>
  );
}

export function BillingView() {
  const plans = useProcedure<Plans>(() => api.manage.billing.plans(), []);
  const invoices = useProcedure(() => api.manage.billing.invoices({ limit: 12 }), []);
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');

  return (
    <Screen layout="column">
      <div style={css('display:flex;align-items:flex-start;gap:14px;flex-wrap:wrap')}>
        <div style={css('min-width:0')}>
          <SectionLabel>Manage · Billing &amp; Plans</SectionLabel>
          <div style={css('font-size:15px;font-weight:500;letter-spacing:-0.01em;margin-top:7px')}>
            Your plan, what it unlocks, and what you have paid
          </div>
          <MutedNote extra="max-width:70ch">
            Changing plan opens the payment provider&rsquo;s own checkout — card details are never entered
            here. Nothing is unlocked until the provider confirms the payment, so a plan you have just bought
            reads as pending until it is real.
          </MutedNote>
        </div>
        <div style={css('flex:1')} />
        <SimulatedBadge what="payments" />
      </div>

      {/* Billing interval. The saving is stated on the control rather than buried in the cards, because it is the
          reason to choose one — and a price that changes when you toggle without saying why reads as a trick. */}
      <div style={css('display:flex;align-items:center;gap:10px;flex-wrap:wrap')}>
        <Segmented>
          {(['monthly', 'yearly'] as const).map((option) => (
            <SegmentedOption
              key={option}
              bg={interval === option ? 'var(--surface)' : 'transparent'}
              fg={interval === option ? 'var(--text)' : 'var(--muted)'}
              fw={interval === option ? 600 : 500}
              aria-pressed={interval === option}
              onClick={() => setInterval(option)}
            >
              {option === 'monthly' ? 'Monthly' : 'Yearly'}
            </SegmentedOption>
          ))}
        </Segmented>
        <span style={css('font-size:11px;color:var(--muted)')}>
          Paying for the year charges at least {YEARLY_SAVING_PCT}% less every month.
        </span>
      </div>

      {/* ── Plans ─────────────────────────────────────────────────────────────────────────────────────────── */}
      {plans.state.status === 'loading' ? (
        <Card padding="roomy">
          <LoadingSkeleton rows={4} label="Loading plans" />
        </Card>
      ) : plans.state.status === 'failed' ? (
        <Card padding="roomy">
          <FailedState what="Your plan" detail={plans.state.error.message} onRetry={plans.reload} />
        </Card>
      ) : (
        <PlanGrid data={plans.state.data} interval={interval} onChanged={plans.reload} />
      )}

      {/* ── Invoices ──────────────────────────────────────────────────────────────────────────────────────── */}
      <Card padding="roomy">
        <CardTitle icon="ph-receipt" iconColor="var(--slate)">
          Invoices
        </CardTitle>
        <CardSubtitle>Every charge, with the plan it paid for.</CardSubtitle>
        <SectionRule />
        {invoices.state.status === 'loading' ? (
          <LoadingSkeleton rows={4} label="Loading invoices" />
        ) : invoices.state.status === 'failed' ? (
          <FailedState what="Your invoices" detail={invoices.state.error.message} onRetry={invoices.reload} />
        ) : invoices.state.data.items.length === 0 ? (
          <MutedNote>No invoices yet. The first one appears after your first paid period.</MutedNote>
        ) : (
          <div>
            {invoices.state.data.items.map((invoice, i, all) => (
              <ListRow key={invoice.id} last={i === all.length - 1}>
                <span style={css('font-size:12px;font-weight:500;width:120px')}>{invoice.number}</span>
                <TabularCell width={92}>{invoice.issuedAt.slice(0, 10)}</TabularCell>
                <span style={css('flex:1')} />
                <TabularCell color="var(--text)" width={72}>
                  {money(invoice.amount, invoice.currency)}
                </TabularCell>
                <InvoiceStatus status={invoice.status} />
              </ListRow>
            ))}
          </div>
        )}
      </Card>
    </Screen>
  );
}

function PlanGrid({
  data,
  interval,
  onChanged,
}: {
  data: Plans;
  interval: 'monthly' | 'yearly';
  onChanged: () => void;
}) {
  const ordered = [...data.plans].sort((a, b) => (TIER_ORDER[a.tier] ?? 9) - (TIER_ORDER[b.tier] ?? 9));
  const current = ordered.find((p) => p.current);

  return (
    <>
      <Card padding="roomy" fullWidth>
        <div style={css('display:flex;align-items:center;gap:10px;flex-wrap:wrap')}>
          <CardTitle icon="ph-credit-card" iconColor="var(--slate)">
            Current plan
          </CardTitle>
          <span style={css('flex:1')} />
          <SubscriptionState status={data.subscription.status} renewsAt={data.subscription.renewsAt} />
        </div>
        <CardSubtitle>
          {current
            ? `${current.name} — ${money(priceFor(current, data.subscription.interval), current.currency)} a month${
                data.subscription.interval === 'yearly' ? ', billed yearly' : ''
              }`
            : 'No plan'}
        </CardSubtitle>
      </Card>

      <div
        style={css(
          'display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:12px;align-items:stretch',
        )}
      >
        {ordered.map((plan) => {
          const diff = data.diff[plan.id];
          return (
            <Card key={plan.id} padding="roomy">
              <div style={css('display:flex;align-items:center;gap:8px')}>
                <CardTitle icon={plan.current ? 'ph-check-circle' : 'ph-circle'} iconColor="var(--slate)">
                  {plan.name}
                </CardTitle>
                <span style={css('flex:1')} />
                {plan.current ? (
                  <StatusChip fg="var(--pos)" bg="color-mix(in srgb, var(--pos) 12%, transparent)">
                    current
                  </StatusChip>
                ) : plan.recommended ? (
                  <StatusChip fg="var(--acc)" bg="var(--acc-fill)">
                    recommended
                  </StatusChip>
                ) : null}
              </div>

              <div style={css('display:flex;align-items:baseline;gap:6px;margin-top:9px')}>
                <span
                  style={css(
                    'font-size:22px;font-weight:600;letter-spacing:-0.02em;font-variant-numeric:tabular-nums',
                  )}
                >
                  {money(priceFor(plan, interval), plan.currency)}
                </span>
                <span style={css('font-size:11px;color:var(--muted)')}>
                  per month{interval === 'yearly' ? ', billed yearly' : ''}
                </span>
              </div>

              {/* The plan's own words, and the tier it builds on. */}
              <MutedNote>{plan.blurb}</MutedNote>
              <div style={css('display:flex;flex-direction:column;gap:6px;margin-top:11px')}>
                {plan.inherits ? (
                  <div style={css('font-size:11.5px;color:var(--text-mid);font-weight:500')}>
                    Everything in {plan.inherits}
                  </div>
                ) : null}
                {plan.highlights.map((highlight) => (
                  <div key={highlight} style={css('display:flex;gap:7px;align-items:flex-start')}>
                    <i
                      className="ph ph-check"
                      style={css('font-size:12px;color:var(--pos);margin-top:2px;flex:none')}
                    ></i>
                    <span style={css('font-size:11.5px;color:var(--text2);line-height:1.5')}>
                      {highlight}
                    </span>
                  </div>
                ))}
              </div>

              {/* The entitlement diff: what actually changes in the product, as distinct from the selling points. */}
              {diff && (diff.gained.length > 0 || diff.lost.length > 0) ? (
                <Inset filled extra="margin-top:11px">
                  {diff.gained.length > 0 ? (
                    <div style={css('display:flex;gap:7px;flex-wrap:wrap;align-items:center')}>
                      <span style={css('font-size:10px;color:var(--pos);font-weight:600')}>Gains</span>
                      {diff.gained.map((feature) => (
                        <OutlineChip key={feature}>{feature}</OutlineChip>
                      ))}
                    </div>
                  ) : null}
                  {diff.lost.length > 0 ? (
                    <div
                      style={css(
                        `display:flex;gap:7px;flex-wrap:wrap;align-items:center${diff.gained.length ? ';margin-top:8px' : ''}`,
                      )}
                    >
                      <span style={css('font-size:10px;color:var(--neg);font-weight:600')}>Loses</span>
                      {diff.lost.map((feature) => (
                        <OutlineChip key={feature}>{feature}</OutlineChip>
                      ))}
                    </div>
                  ) : null}
                </Inset>
              ) : (
                <LimitsDelta plan={plan} current={current} />
              )}

              <FooterRow>
                <span style={css('flex:1')} />
                <PlanAction plan={plan} interval={interval} onChanged={onChanged} />
              </FooterRow>
            </Card>
          );
        })}
      </div>

      <ManageSubscription />
    </>
  );
}

/** When two plans include the same modules, the honest difference is in their limits — so say which ones move and by
 *  how much, rather than printing "everything your plan includes" and leaving the reader to diff three numbers. */
function LimitsDelta({ plan, current }: { plan: Plan; current: Plan | undefined }) {
  if (!current || plan.id === current.id)
    return <MutedNote>Everything your current plan includes.</MutedNote>;

  const rows: string[] = [];
  const compare = (label: string, from: number, to: number, unit: string) => {
    if (from === to) return;
    rows.push(`${label} ${from}${unit} → ${to}${unit}`);
  };
  compare('Accounts', current.limits.accountSlots, plan.limits.accountSlots, '');
  compare('Voice', current.limits.voiceMinutes, plan.limits.voiceMinutes, ' min');
  compare('AI', current.limits.aiDailyBudget, plan.limits.aiDailyBudget, '/day');

  if (rows.length === 0) return <MutedNote>Identical to your current plan.</MutedNote>;
  return (
    <Inset filled extra="margin-top:11px">
      <div style={css('display:flex;gap:7px;flex-wrap:wrap;align-items:center')}>
        <span style={css('font-size:10px;color:var(--muted2);font-weight:600')}>Same modules, more room</span>
        {rows.map((row) => (
          <OutlineChip key={row}>{row}</OutlineChip>
        ))}
      </div>
    </Inset>
  );
}

function SubscriptionState({ status, renewsAt }: { status: string; renewsAt: string | null }) {
  const tone =
    status === 'active' || status === 'trialing'
      ? { fg: 'var(--pos)', bg: 'color-mix(in srgb, var(--pos) 12%, transparent)' }
      : status === 'pending_webhook'
        ? { fg: 'var(--amb)', bg: 'color-mix(in srgb, var(--amb) 12%, transparent)' }
        : { fg: 'var(--neg)', bg: 'color-mix(in srgb, var(--neg) 12%, transparent)' };
  return (
    <div style={css('display:flex;align-items:center;gap:9px')}>
      <StatusChip fg={tone.fg} bg={tone.bg}>
        {status === 'pending_webhook' ? 'awaiting payment' : status.replace('_', ' ')}
      </StatusChip>
      {renewsAt ? (
        <span style={css('font-size:11px;color:var(--muted)')}>renews {renewsAt.slice(0, 10)}</span>
      ) : null}
    </div>
  );
}

function PlanAction({
  plan,
  interval,
  onChanged,
}: {
  plan: Plan;
  interval: 'monthly' | 'yearly';
  onChanged: () => void;
}) {
  const checkout = async () => {
    // The key is per plan *and* interval, not per click: a double-click must reach the checkout it already opened
    // rather than opening a second one, while switching to yearly is a genuinely different claim (REQ-LADDER).
    const result = await api.manage.billing.checkout({
      idempotencyKey: `checkout-${plan.id}-${interval}`,
      planId: plan.id,
      interval,
    });
    onChanged();
    if (result.url) window.location.assign(result.url);
  };
  // The current plan gets a statement, not a disabled button: the reference has no disabled-button treatment, and
  // inventing one here would put a style in the product that no canonical surface authorises.
  if (plan.current) return <MutedNote extra="margin:0">This is your plan.</MutedNote>;
  return <AccentButton onClick={() => void checkout()}>Choose</AccentButton>;
}

/** What a month of this plan costs at the chosen interval. */
function priceFor(plan: Plan, interval: 'monthly' | 'yearly'): number {
  return interval === 'yearly' ? plan.priceMonthlyYearly : plan.priceMonthly;
}

function ManageSubscription() {
  const openPortal = async () => {
    const result = await api.manage.billing.portal({ idempotencyKey: 'billing-portal' });
    if (result.url) window.location.assign(result.url);
  };
  return (
    <Card padding="roomy">
      <CardTitle icon="ph-arrow-square-out" iconColor="var(--slate)">
        Payment details
      </CardTitle>
      <CardSubtitle>
        Cards, billing address and receipts live with the payment provider, not here — so this app never holds
        them.
      </CardSubtitle>
      <FooterRow>
        <span style={css('flex:1')} />
        <GhostButton onClick={() => void openPortal()}>Open billing portal</GhostButton>
      </FooterRow>
    </Card>
  );
}
